/* eslint-disable max-len */ // TODO: Remove this comment once the code is refactored
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useMemo, useState } from 'react';

import { renderToString } from 'react-dom/server';
import i18n from 'i18n-react';

import badges from 'assets/images/badges';

import BadgeDisplay from 'components/BadgeDisplay';
import ContentHeader from 'components/ContentHeader';
import Diploma from 'components/Diploma';
import Modal from 'components/Modal';
import RewardMenu from 'components/RewardMenu';
import ShareBox from 'components/ShareBox';
import SharingPopup from 'components/SharingPopup';

import { useChapters } from 'hooks/useChapters';
import { usePointsHandler } from 'hooks/usePointsHandler';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import Badge from 'views/BadgeView/Badge';
import BadgeContainer from 'views/BadgeView/BadgeContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const params = new URLSearchParams(window.location.search);
let mobile = false;
if (params.get('mobile')) {
  mobile = true;
}

/**
 * BadgeView
 *
 * A view component that displays each attained badge for the user. These badges being:
 *
 * - Bronze/Silver/Gold/Platinum badges: Based on the amount of completed trainings
 * - Specialization/Province badges: Based on completing specific specialization training chapters
 * - Specialization/Province completion: Obtained when completing both the main chapter and at least 1 city subchapter for
 *   that province.
 *
 * @returns {JSX.Element} A component that displays the user's badges and progress.
 */
const BadgeView = () => {
  const { tierBadges } = badges;

  const [showDiploma, setShowDiploma] = useState(false);
  const [showBadgePopout, setShowBadgePopout] = useState(false);

  const [selectedBadge, setSelectedBadge] = useState({ url: '', title: '' });

  const {
    appId,
    lang: { code, langFile },
  } = useSettings();

  const { user } = useUserInfo();
  const { diplomaRate } = useChapters();

  const { tiers, completedItems, totalPoints, getTieredValue, loaded: loadedPointsHandler } = usePointsHandler();

  const actions = useMemo(() => ['intro-chapter', 'specialization', 'subapp'], []);

  const tierIndex = tiers && Math.min(getTieredValue(), tiers.length - 1);
  const currentPath = window.location.pathname;
      
  const handleDiplomaOpen = () => setShowDiploma(true);
  const handleDiplomaClose = () => setShowDiploma(false);

  const handleBadgeOpen = (badgeData) => {
    const { url, title } = badgeData;
    setSelectedBadge({ url, title });
    setShowBadgePopout(true);
  };
  const handleBadgeClose = () => setShowBadgePopout(false);

  const renderBadges = useCallback(
    (tierName) => {
      if (!loadedPointsHandler) return null;

      return completedItems
        .filter(({ tier, badge }) => tier?.toLowerCase() === tierName.toLowerCase() && !!badge)
        .map(({ id, badge, region }) => (
          <Badge
            key={id}
            badgeImg={badge}
            active
            rounded={false}
            onClick={() => handleBadgeOpen({ url: badge, title: '' })}
            region={region}
          />
        ));
    },
    [completedItems, loadedPointsHandler],
  );

  const renderLevels = useCallback(
    (tierName) => {
      if (!loadedPointsHandler) return null;

      return completedItems
        .filter(({ action, badge }) => action?.toLowerCase() === tierName.toLowerCase() && !!badge)
        .map(({ id, badge, region }) => (
          <Badge
            key={id}
            badgeImg={badge}
            active
            rounded={false}
            onClick={() => handleBadgeOpen({ url: badge, title: '' })}
            region={region}
          />
        ));
    },
    [completedItems, loadedPointsHandler],
  );

  /**
   * The memoized rendered tiers, with all obtained badges grouped by tier. The first tier is always included and holds
   * the diploma badge. Curently excludes any other tiers that don't have any badges.
   */
  const renderedTiers = useMemo(() => {
    if (!loadedPointsHandler) return null;

    const badgeContainers = tiers.reduce((acc, tier, index) => {
      const { copy, title } = tier;
      const renderedBadges = renderBadges(title);

      // Only push last one
      if (renderedBadges.length > 0 && index === tierIndex && tier.completedItems === tier.count) {
        acc.push(
          <React.Fragment key={title}>
            <div key={title} className="badge-display__wrapper">
              <h1 className="badgeView__text">{copy}</h1>
            </div>
            <hr className="colored" />
          </React.Fragment>,
        );
      }
      return acc;
    }, []);

    // Certifications (action: intro-chapter), Experience badges (action: specialization), and Specialization badges (action: subapp)
    const badgeItems = actions.reduce((acc, action, index) => {
      const renderedBadges = renderLevels(action); // Assuming this returns an array of badge components with a region key

      const diplomaBadgeTier = index === 0;
      const diplomaCompleted = diplomaRate >= 100;

      const image = `https://cms.travpromobile.com/api/app/diploma?first_name=preview&last_name=account&app_id=1441&lang=${langFile}_${langFile.toUpperCase()
        .replace('PT', 'BR')
        .replace('KO', 'KR')}`;

      if (!diplomaCompleted) {
        acc.push(<div className="badge-display__wrapper" />);
        return acc;
      }

      if (action === 'subapp') {
        // Group badges by region
        const groupedBadges = renderedBadges.reduce((group, badge) => {
          const regionKey = badge.props.region && badge.props.region.toLowerCase(); // Replace 'badge.props.region' with the actual way to access the region key in your badge component
          const region = (regionKey && i18n.translate(`missing.${regionKey}`)) || i18n.translate('badgeView.other');
          const newGroup = { ...group };
          newGroup[region] = newGroup[region] || [];
          newGroup[region].push(badge);
          return newGroup;
        }, {});

        // if (renderedBadges.length > 0) acc.push(<h2 className="badgeView__sub">{title}</h2>);
        if (renderedBadges.length > 0) acc.push(<hr className="top-margin" />);

        // Render badges grouped by region
        Object.entries(groupedBadges).forEach(([region, regionBadges]) => {
          acc.push(
            <React.Fragment key={`${action}-${region}`}>
              <h3 className="badgeView__region__title">{region}</h3>
              <div className="container__badges">{regionBadges}</div>
            </React.Fragment>,
          );
        });
        return acc;
      }

      if (action !== 'subapp' && (renderedBadges.length > 0 || diplomaBadgeTier)) {
        acc.push(
          <React.Fragment key={action}>
            {/* <h2 className="badgeView__sub">{title}</h2> */}
            <hr className="top-margin" />
            <BadgeContainer key={`badge-container-${action}`} showLine>
              <div className="container__badges">
                {diplomaBadgeTier && (
                  <a
                    className={`container__progressDiploma ${diplomaCompleted ? 'completed' : ''}`}
                    onClick={handleDiplomaOpen}
                  >
                    <img
                      // eslint-disable-next-line max-len
                      src={image}
                      alt="diploma"
                    />
                  </a>
                )}
                {renderedBadges}
              </div>
            </BadgeContainer>
          </React.Fragment>,
        );
      }
      return acc;
    }, []);

    return (
      <div className="badgeContainer points__container">
        <div className="badgeView__badge-display">
          {badgeContainers}
          {badgeItems}
        </div>
      </div>
    );
  }, [loadedPointsHandler, tiers, actions, renderBadges, tierIndex, renderLevels, diplomaRate, langFile, code]);

  return (
    <div className="contentContainer badgeView">
      {currentPath === '/main/rewards/achievements' && <RewardMenu />}

      <Modal show={showBadgePopout} onHide={handleBadgeClose} bgClose autosize>
        {/* FIXME: TEMP Props */}
        <ShareBox
          image={selectedBadge.url}
          url={selectedBadge.url}
          title={selectedBadge.title}
          subject={i18n.translate('sharing.shareSubjectBadge')}
          body={i18n.translate('sharing.shareBodyBadge', { chapterTitle: selectedBadge.title })}
          email
          download
          size="sm"
        />
      </Modal>

      <ContentHeader header={i18n.translate('badgeView.title')} subtitle={i18n.translate('badgeView.subtitle')} />
      <hr />

      <div className="content-box">
        {loadedPointsHandler &&
          tiers && ( // Check if the data has been loaded and tiers are available
            <div className="badgeContainer points__container">
              <h1 className="badgeView__title--badge-display secondary">
                {i18n.translate('badgeView.badgeDisplayTitle')}
              </h1>
              <BadgeDisplay
                tierBadges={tierBadges}
                tiers={tiers}
                tierIndex={tierIndex}
                totalPoints={totalPoints}
                layout="longBar"
                hideBadge
                completedItems={completedItems}
                hasPopout
              />
              <div className="badgeView__title--tiers">
                {tiers.map((tier, index) => (
                  <div
                    key={tier.title}
                    className={`tier-title ${tierIndex === index ? 'active' : ''} ${
                      tier.completedItems >= tier.count ? '' : 'disabled'
                    }`}
                  >
                    {!mobile && (
                      <a
                        href={Object.values(tierBadges)[index]}
                        data-fancybox={tier.title}
                        data-options={JSON.stringify({
                          buttons: ['download', 'share', 'close'],
                          share: {
                            tpl: renderToString(<SharingPopup url={Object.values(tierBadges)[index]} downloadable={false}/>),
                          },
                          btnTpl: {
                            download: renderToString(
                              <a
                                href={`https://stack.travpromobile.com/download.php?url=${Object.values(tierBadges)[index]}&name=${tier.title}`}
                              >
                                <button type='button' data-fancybox-download className='fancybox-button fancybox-button--download' title='Download'>
                                  <FontAwesomeIcon icon={['fas', "download"]} />
                                </button>
                              </a>
                            )
                          },
                        })}
                      >
                        <img
                          className="badge"
                          src={Object.values(tierBadges)[index]}
                          alt="Logo"
                        />
                      </a>
                    )}
                    {mobile && (
                      <a onClick={() => window.ReactNativeWebView.postMessage(`webview:${Object.values(tierBadges)[index]}`)}>
                        <img
                          className="badge"
                          src={Object.values(tierBadges)[index]}
                          alt="Logo"
                        />
                      </a>
                    )}
                    <span className="title">
                      {Math.min(tier.completedItems, tier.count)}
                      {' / '}
                      {tier.count} {i18n.translate('badgeView.complete')}
                    </span>
                  </div>
                ))}
              </div>
              <Modal show={showDiploma} onHide={handleDiplomaClose} bgClose autosize>
                <Diploma
                  appId={appId}
                  firstName={user.first_name}
                  lastName={user.last_name}
                  email={user.email}
                  key={user.firstName}
                />
              </Modal>
            </div>
          )}

        {renderedTiers}
      </div>
      {mobile && (
        <style>
          {
            '\
      .buttons__diploma { display: none !important; } .sharebox__buttons { display: none !important; } .contentContainer { margin-top: 5rem; } .header { display: none; } .cookieConsent { display: none; }'
          }
        </style>
      )}
    </div>
  );
};

export default BadgeView;
